import React from "react";
import { Button } from "./ui/button";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { useLocation } from "react-router-dom";

const Footer = () => {
  const location = useLocation();

  const handleSectionClick = (sectionId) => {
    if (location.pathname === '/') {
      // Si estamos en la home, hacer scroll directamente
      document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
    } else {
      // Si estamos en otra página, navegar a home con hash
      window.location.href = `/#${sectionId}`;
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-4xl font-bold mb-6">
            ¿Preparado para revolucionar tu empresa?
          </h2>
          <p className="text-lg lg:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Reserva tu sesión estratégica gratuita y descubre el potencial oculto 
            de tu negocio con tecnología inteligente.
          </p>
          <Button 
            size="lg"
            className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-4 font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            onClick={() => window.open('https://cal.com/peter-gal/30min?overlayCalendar=true', '_blank')}
          >
            Agenda tu consulta gratuita
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">I2M</span>
                </div>
                <span className="text-xl font-semibold">Innova2Market</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Revolucionamos empresas con IA que trabaja 24/7. Desde asistentes 
                telefónicos hasta automatización de ecommerce, transformamos tu 
                negocio en una máquina de crecimiento.
              </p>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Servicios</h3>
              <ul className="space-y-3 text-gray-400">
                <li 
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => handleSectionClick('servicios')}
                >
                  Asistentes telefónicos
                </li>
                <li 
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => handleSectionClick('servicios')}
                >
                  Conversaciones automatizadas
                </li>
                <li 
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => handleSectionClick('ecommerce')}
                >
                  Automatización Ecommerce
                </li>
                <li 
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => handleSectionClick('proceso')}
                >
                  Proceso de transformación
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Empresa</h3>
              <ul className="space-y-3 text-gray-400">
                <li 
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => window.location.href = '/sobre-nosotros'}
                >
                  Sobre nosotros
                </li>
                <li 
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => window.location.href = '/casos-exito'}
                >
                  Casos de éxito
                </li>
                <li 
                  className="hover:text-white transition-colors cursor-pointer"
                  onClick={() => window.location.href = '/contacto'}
                >
                  Contacto
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contacto</h3>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5" />
                  <span className="hover:text-white transition-colors cursor-pointer">
                    info@innova2market.es
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5" />
                  <span className="hover:text-white transition-colors cursor-pointer">
                    +34 604 97 16 08
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5" />
                  <span className="hover:text-white transition-colors cursor-pointer">
                    España
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 py-6 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4">
            <p>&copy; 2025 Innova2Market. Todos los derechos reservados.</p>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <span>Powered by</span>
              <img 
                src="/agiliacenter-logo.webp" 
                alt="AgiliaCenter - Desarrollado por AgiliaCenter" 
                className="h-4 opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <span 
              className="hover:text-white transition-colors cursor-pointer"
              onClick={() => window.location.href = '/politica-privacidad'}
            >
              Política de privacidad
            </span>
            <span 
              className="hover:text-white transition-colors cursor-pointer"
              onClick={() => window.location.href = '/terminos-uso'}
            >
              Términos de uso
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;